import React, { useState, useCallback } from 'react';
import axios from 'axios';
import WordHistory from './WordHistory';
import './WordLookup.css';
import FlipBook from './FlipBook';

// Declare gtag for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface WordEntry {
  word: string;
  explanation: string;
  timestamp: Date;
}

const WordLookup: React.FC = () => {
  const [word, setWord] = useState('');
  const [explanation, setExplanation] = useState('');
  const [currentWord, setCurrentWord] = useState('');
  const [loading, setLoading] = useState(false);
  const [wordHistory, setWordHistory] = useState<WordEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Use relative path for API calls when deployed with Firebase Hosting
  // Firebase will proxy /api/** to Cloud Run backend
  // For local development, use the environment variable
  const API_BASE = process.env.REACT_APP_API_BASE_URL 
    ? (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '')
    : ''; // Empty string means relative path (same domain)
    
  const lookupWord = useCallback(async (forceRegenerate = false) => {
    if (!word.trim()) return;

    setLoading(true);
    try {
      const url = `${API_BASE}/api/explain`;
      const response = await axios.post(url, { 
        word: word.trim(),
        force_regenerate: forceRegenerate 
      });
      
      const newExplanation = response.data.explanation;
      
      setCurrentWord(word.trim());
      setExplanation(newExplanation);
      
      // Track event in Google Analytics
      if (window.gtag) {
        window.gtag('event', forceRegenerate ? 'regenerate_word' : 'lookup_word', {
          event_category: 'Word Lookup',
          event_label: word.trim()
        });
      }
      
      // Add to history
      const newEntry: WordEntry = {
        word: word.trim(),
        explanation: newExplanation,
        timestamp: new Date()
      };
      setWordHistory(prev => [newEntry, ...prev.slice(0, 19)]); // Keep last 20 entries
    } catch (error) {
      setExplanation('Oops! Something went wrong. Please try again!');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [word, API_BASE]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    lookupWord();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      lookupWord();
    }
  };

  return (
    <div className="word-lookup">
      <div className="lookup-container">
        <p className="subtitle">Type any word or phrase and I'll explain it to you!</p>
        
        <form onSubmit={handleSubmit} className="word-form">
          <div className="input-container">
            <input
              type="text"
              value={word}
              onChange={(e) => {
                // Allow only alphanumeric, spaces, hyphens, quotes, periods, slashes, commas, and ampersands
                const filtered = e.target.value.replace(/[^a-zA-Z0-9\s\-'"./,&]/g, '');
                setWord(filtered);
              }}
              onKeyPress={handleKeyPress}
              placeholder="Type a word/phrase here..."
              className="word-input"
              disabled={loading}
              maxLength={40}
            />
            <button 
              type="submit" 
              className="lookup-button"
              disabled={loading || !word.trim()}
            >
              {loading ? '🔍 Looking...' : '🔍 Explain!'}
            </button>
          </div>
        </form>

        {explanation && (
          <div className="explanation-container">
            <FlipBook heading="📖 Here's what I found:" text={explanation} pageKey={currentWord} />
            <div className="explanation-actions">
              <button 
                className="regenerate-button"
                onClick={() => lookupWord(true)}
                disabled={loading}
                title="Generate a new explanation for this word"
              >
                {loading ? '🔄 Regenerating...' : '🔄 Try Another Explanation'}
              </button>
            </div>
          </div>
        )}

        {wordHistory.length > 0 && (
          <div className="history-section">
            <button 
              className="history-toggle"
              onClick={() => setShowHistory(!showHistory)}
            >
              📚 {showHistory ? 'Hide' : 'Show'} My Word History ({wordHistory.length})
            </button>
            
            {showHistory && (
              <WordHistory words={wordHistory} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WordLookup;